import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  LogOut,
  Euro,
  Settings,
  BarChart3,
  Mail,
  PhoneCall,
  PhoneMissed,
  PhoneForwarded,
  MessageSquare,
} from 'lucide-react';
import axios from 'axios';
import {
  EditLeadModal,
  StatsCards,
  LeadsTab,
  ContactMessagesTab,
  AnalyticsTab,
  SettingsTab,
} from '../components/admin';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminDashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [stats, setStats] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('leads');
  const [settings, setSettings] = useState({
    google_analytics_id: '',
    conversion_event_name: '',
    conversion_value: '',
    meta_title: '',
    meta_description: '',
    meta_keywords: '',
    notification_email: '',
    sender_email: '',
    smtp_host: '',
    smtp_port: '587',
    smtp_username: '',
    smtp_password: '',
    smtp_use_tls: 'true',
  });
  const [isSavingSettings, setIsSavingSettings] = useState(false);
  const [formAnalytics, setFormAnalytics] = useState(null);
  const [showSmtpPassword, setShowSmtpPassword] = useState(false);
  const [testEmailAddress, setTestEmailAddress] = useState('');
  const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [expandedLeadId, setExpandedLeadId] = useState(null);
  const [leadActions, setLeadActions] = useState({});
  const [commissionStats, setCommissionStats] = useState({ total: { all: 0 }, month: { all: 0 }, quarter: { all: 0 } });
  const [commissionPeriod, setCommissionPeriod] = useState('total');
  const [assignedFilter, setAssignedFilter] = useState('all');

  const fetchDashboardData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [leadsRes, contactRes, statsRes, settingsRes, commissionRes] = await Promise.all([
        axios.get(`${API}/leads`),
        axios.get(`${API}/contact`),
        axios.get(`${API}/stats`),
        axios.get(`${API}/settings`).catch(() => ({ data: {} })),
        axios.get(`${API}/leads/stats/commission`).catch(() => ({ data: { total: { all: 0 }, month: { all: 0 }, quarter: { all: 0 } } })),
      ]);

      setLeads(leadsRes.data);
      setContactMessages(contactRes.data);
      setStats(statsRes.data);
      setFormAnalytics(statsRes.data.form_abandonment);
      setCommissionStats(commissionRes.data);

      if (settingsRes.data) {
        setSettings((prev) => ({ ...prev, ...settingsRes.data }));
      }
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Kon dashboard gegevens niet laden');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/admin');
      return;
    }
    fetchDashboardData();
  }, [isAuthenticated, navigate, fetchDashboardData]);

  const updateLeadStatus = async (leadId, status, assignedTo = null) => {
    try {
      const updateData = { status };
      if (assignedTo !== null) updateData.assigned_to = assignedTo;

      await axios.put(`${API}/leads/${leadId}`, updateData);

      setLeads(leads.map((lead) =>
        lead.id === leadId ? { ...lead, status, assigned_to: assignedTo || lead.assigned_to } : lead
      ));

      toast.success('Lead status bijgewerkt');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to update lead:', error);
      toast.error('Kon lead niet bijwerken');
    }
  };

  const deleteLead = async (leadId, leadName) => {
    if (!window.confirm(`Bent u zeker dat u de lead van ${leadName} permanent wilt verwijderen? Deze actie kan niet ongedaan gemaakt worden (GDPR).`)) {
      return;
    }

    try {
      await axios.delete(`${API}/leads/${leadId}`);
      setLeads(leads.filter((lead) => lead.id !== leadId));
      toast.success('Lead permanent verwijderd');
      fetchDashboardData();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast.error('Kon lead niet verwijderen');
    }
  };

  const deleteContactMessage = async (messageId) => {
    try {
      await axios.delete(`${API}/contact/${messageId}`);
      setContactMessages(contactMessages.filter((msg) => msg.id !== messageId));
      toast.success('Contactbericht verwijderd');
    } catch (error) {
      console.error('Failed to delete contact message:', error);
      toast.error('Kon contactbericht niet verwijderen');
    }
  };

  // Lead Actions functions
  const toggleLeadExpand = async (leadId) => {
    if (expandedLeadId === leadId) {
      setExpandedLeadId(null);
      return;
    }

    setExpandedLeadId(leadId);

    if (!leadActions[leadId]) {
      try {
        const res = await axios.get(`${API}/leads/${leadId}/actions`);
        setLeadActions((prev) => ({ ...prev, [leadId]: res.data }));
      } catch (error) {
        console.error('Failed to fetch lead actions:', error);
      }
    }
  };

  const addLeadAction = async (leadId, actionData) => {
    if (!actionData || !actionData.action_type) {
      toast.error('Selecteer eerst een actie type');
      return;
    }

    try {
      const res = await axios.post(`${API}/leads/${leadId}/actions`, {
        lead_id: leadId,
        action_type: actionData.action_type,
        note: actionData.note,
      });

      setLeadActions((prev) => ({
        ...prev,
        [leadId]: [res.data, ...(prev[leadId] || [])],
      }));

      setLeads(leads.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              last_action: actionData.action_type,
              last_action_at: res.data.created_at,
              last_action_by: res.data.created_by_name,
            }
          : lead
      ));

      toast.success('Actie toegevoegd');
    } catch (error) {
      console.error('Failed to add action:', error);
      toast.error('Kon actie niet toevoegen');
      throw error;
    }
  };

  const deleteLeadAction = async (leadId, actionId) => {
    try {
      await axios.delete(`${API}/leads/${leadId}/actions/${actionId}`);

      const updatedActions = (leadActions[leadId] || []).filter((a) => a.id !== actionId);
      setLeadActions((prev) => ({ ...prev, [leadId]: updatedActions }));

      if (updatedActions.length > 0) {
        const lastAction = updatedActions[0];
        setLeads(leads.map((lead) =>
          lead.id === leadId
            ? {
                ...lead,
                last_action: lastAction.action_type,
                last_action_at: lastAction.created_at,
                last_action_by: lastAction.created_by_name,
              }
            : lead
        ));
      } else {
        setLeads(leads.map((lead) =>
          lead.id === leadId ? { ...lead, last_action: null, last_action_at: null, last_action_by: null } : lead
        ));
      }

      toast.success('Actie verwijderd');
    } catch (error) {
      console.error('Failed to delete action:', error);
      toast.error('Kon actie niet verwijderen');
    }
  };

  const getActionIcon = (actionType) => {
    switch (actionType) {
      case 'Gebeld':
        return <PhoneCall className="w-4 h-4 text-green-600" />;
      case 'Gemaild':
        return <Mail className="w-4 h-4 text-blue-600" />;
      case 'Pakt niet op':
        return <PhoneMissed className="w-4 h-4 text-red-500" />;
      case 'Terugbellen':
        return <PhoneForwarded className="w-4 h-4 text-orange-500" />;
      default:
        return <MessageSquare className="w-4 h-4 text-slate-500" />;
    }
  };

  const formatActionDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('nl-BE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const handleCommissionChange = (leadId, value) => {
    setLeads(leads.map((l) =>
      l.id === leadId ? { ...l, commission: value ? parseFloat(value) : null } : l
    ));
  };

  const updateLeadCommission = async (leadId, commission) => {
    try {
      const commissionDate = commission ? new Date().toISOString() : null;
      await axios.put(`${API}/leads/${leadId}`, {
        commission: commission ? parseFloat(commission) : null,
        commission_date: commissionDate,
      });

      setLeads(leads.map((lead) =>
        lead.id === leadId
          ? { ...lead, commission: commission ? parseFloat(commission) : null, commission_date: commissionDate }
          : lead
      ));

      toast.success('Commissie bijgewerkt');

      const commissionRes = await axios.get(`${API}/leads/stats/commission`);
      setCommissionStats(commissionRes.data);
    } catch (error) {
      console.error('Failed to update commission:', error);
      toast.error('Kon commissie niet bijwerken');
    }
  };

  const getCommissionForPeriod = () => {
    const periodStats = commissionStats[commissionPeriod] || { all: 0, wim: 0, jelle: 0 };
    if (assignedFilter === 'all') return periodStats.all;
    if (assignedFilter === 'wim') return periodStats.wim;
    if (assignedFilter === 'jelle') return periodStats.jelle;
    return periodStats.all;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('nl-BE', { style: 'currency', currency: 'EUR' }).format(amount || 0);
  };

  const saveSettings = async () => {
    setIsSavingSettings(true);
    try {
      const settingPromises = Object.entries(settings).map(([key, value]) =>
        axios.post(`${API}/settings?setting_key=${key}&setting_value=${encodeURIComponent(value || '')}`)
      );

      await Promise.all(settingPromises);
      toast.success('Instellingen opgeslagen');
    } catch (error) {
      console.error('Failed to save settings:', error);
      toast.error('Kon instellingen niet opslaan');
    } finally {
      setIsSavingSettings(false);
    }
  };

  const handleSettingChange = (key, value) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const sendTestEmail = async () => {
    if (!testEmailAddress) {
      toast.error('Vul een email adres in');
      return;
    }

    setIsSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/test-email?test_email=${encodeURIComponent(testEmailAddress)}`);
      toast.success(response.data.message);
      setTestEmailAddress('');
    } catch (error) {
      const message = error.response?.data?.detail || 'Kon test email niet verzenden';
      toast.error(message);
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const sendTestInternalNotification = async () => {
    setIsSendingTestEmail(true);
    try {
      const response = await axios.post(`${API}/test-internal-notification`);
      toast.success(response.data.message);
    } catch (error) {
      const message = error.response?.data?.detail || 'Kon test notificatie niet verzenden';
      toast.error(message);
    } finally {
      setIsSendingTestEmail(false);
    }
  };

  const openEditModal = (lead) => {
    setEditingLead(lead);
    setEditFormData({
      first_name: lead.first_name,
      last_name: lead.last_name,
      email: lead.email,
      phone: lead.phone,
      address: lead.address,
      postcode: lead.postcode,
      customer_type: lead.customer_type,
      current_providers: lead.current_providers || [],
      services: lead.services || [],
      mobile_count: lead.mobile_count || 1,
      tv_decoder_count: lead.tv_decoder_count || 1,
      energy_interest: lead.energy_interest || false,
      current_energy_provider: lead.current_energy_provider || '',
    });
  };

  const closeEditModal = () => {
    setEditingLead(null);
    setEditFormData({});
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData((prev) => ({ ...prev, [field]: value }));
  };

  const saveLeadEdit = async () => {
    try {
      const response = await axios.put(`${API}/leads/${editingLead.id}`, editFormData);
      setLeads(leads.map((l) => (l.id === editingLead.id ? response.data : l)));
      toast.success('Lead succesvol bijgewerkt');
      closeEditModal();
    } catch (error) {
      const message = error.response?.data?.detail || 'Kon lead niet bijwerken';
      toast.error(message);
    }
  };

  const filteredLeads = leads.filter((lead) => {
    const matchesSearch = searchTerm === '' ||
      lead.first_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.last_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      lead.phone.includes(searchTerm);

    const matchesStatus = statusFilter === 'all' || lead.status === statusFilter;

    const matchesAssigned = assignedFilter === 'all' ||
      (lead.assigned_to && lead.assigned_to.toLowerCase().includes(assignedFilter.toLowerCase()));

    return matchesSearch && matchesStatus && matchesAssigned;
  });

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('nl-BE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (!isAuthenticated || isLoading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner mx-auto mb-4" />
          <p className="text-slate-600">Dashboard laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                <Euro className="w-4 h-4 text-white" />
              </div>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Admin Dashboard</h1>
                <p className="text-sm text-slate-600">Welkom, {user?.full_name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/')}>Naar website</Button>
              <Button variant="outline" onClick={logout} data-testid="logout-button">
                <LogOut className="w-4 h-4 mr-2" />
                Uitloggen
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="p-6">
        <StatsCards
          stats={stats}
          commissionPeriod={commissionPeriod}
          setCommissionPeriod={setCommissionPeriod}
          assignedFilter={assignedFilter}
          setAssignedFilter={setAssignedFilter}
          getCommissionForPeriod={getCommissionForPeriod}
          formatCurrency={formatCurrency}
        />

        {/* Tabs */}
        <div className="mb-6">
          <div className="flex gap-4 border-b">
            <button
              className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'leads' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('leads')}
              data-testid="leads-tab"
            >
              Leads ({leads.length})
            </button>
            <button
              className={`pb-3 px-1 border-b-2 font-medium text-sm ${activeTab === 'contact' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('contact')}
              data-testid="contact-tab"
            >
              Contactberichten ({contactMessages.length})
            </button>
            <button
              className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === 'analytics' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('analytics')}
              data-testid="analytics-tab"
            >
              <BarChart3 className="w-4 h-4" />
              Analytics
            </button>
            <button
              className={`pb-3 px-1 border-b-2 font-medium text-sm flex items-center gap-2 ${activeTab === 'settings' ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              onClick={() => setActiveTab('settings')}
              data-testid="settings-tab"
            >
              <Settings className="w-4 h-4" />
              Instellingen
            </button>
          </div>
        </div>

        {activeTab === 'leads' && (
          <LeadsTab
            leads={leads}
            filteredLeads={filteredLeads}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            statusFilter={statusFilter}
            setStatusFilter={setStatusFilter}
            onRefresh={fetchDashboardData}
            expandedLeadId={expandedLeadId}
            leadActions={leadActions}
            onToggleExpand={toggleLeadExpand}
            onUpdateStatus={updateLeadStatus}
            onDeleteLead={deleteLead}
            onEditLead={openEditModal}
            onAddAction={addLeadAction}
            onDeleteAction={deleteLeadAction}
            onCommissionChange={handleCommissionChange}
            onCommissionBlur={updateLeadCommission}
            getActionIcon={getActionIcon}
            formatDate={formatDate}
            formatActionDate={formatActionDate}
            formatCurrency={formatCurrency}
          />
        )}

        {activeTab === 'contact' && (
          <ContactMessagesTab
            contactMessages={contactMessages}
            deleteContactMessage={deleteContactMessage}
            formatDate={formatDate}
          />
        )}

        {activeTab === 'analytics' && (
          <AnalyticsTab stats={stats} formAnalytics={formAnalytics} />
        )}

        {activeTab === 'settings' && (
          <SettingsTab
            settings={settings}
            handleSettingChange={handleSettingChange}
            saveSettings={saveSettings}
            isSavingSettings={isSavingSettings}
            showSmtpPassword={showSmtpPassword}
            setShowSmtpPassword={setShowSmtpPassword}
            testEmailAddress={testEmailAddress}
            setTestEmailAddress={setTestEmailAddress}
            sendTestEmail={sendTestEmail}
            sendTestInternalNotification={sendTestInternalNotification}
            isSendingTestEmail={isSendingTestEmail}
          />
        )}
      </div>

      <EditLeadModal
        editingLead={editingLead}
        editFormData={editFormData}
        onClose={closeEditModal}
        onSave={saveLeadEdit}
        onFormChange={handleEditFormChange}
      />
    </div>
  );
};

export default AdminDashboard;
